'use client';
import React, { useState } from 'react';

import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useUIListener } from '@repo/ui/src/modules/UI/hooks/useUIListener';
import { useDelayedCB } from '@hooks/useDelayedCB';
import { ModalID, useDrawer, useModal } from '@repo/ui/src/modules/UI';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useBasket from '@hooks/basket/useBasket';
import Auth from '@components/Auth';
import useGoToCheckout from '@hooks/basket/useGoToCheckout';
import { useCustomer } from '@hooks/customer';
import useUpdateBasket from '@hooks/basket/useUpdateBasket';
import { ErrorState } from '@repo/ui/src/components/Form';
import { Cart as SharedCart } from '@repo/ui/src/components/Cart';
import { Markdown } from '@repo/ui/src/components/Markdown';
import Image from '@repo/ui/src/components/Image';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ImageProps } from '@repo/ui/src/types/image';
import LoadingLogo from '@components/Loading/LoadingLogo';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { LinkProps } from '@repo/ui/src/types/link';
import CartPageView from './_CartPageView';
import {
  basketToCartLineItems,
  basketToCartTotals,
  getOutOfStockIds,
  logCartViewed,
} from './helpers';

const Cart = () => {
  const { inDrawer, close } = useDrawer();
  const { push: pushCheckoutLoader, close: closeCheckoutLoader } = useModal(ModalID.checkoutLoader);
  const { data: basket, isLoading } = useBasket();
  const { trigger: goToCheckout, isMutating } = useGoToCheckout();
  const { mutate: mutateCustomer, data: user } = useCustomer();
  const { trigger: updateBasket } = useUpdateBasket();
  const { t } = useTranslation();
  const { push: pushErrorModal } = useModal(ModalID.error);
  const { locale, currency, push } = useCurrentUrl();
  const [isAuthStep, setIsAuthStep] = useState(false);
  const [checkoutError, setCheckoutError] = React.useState(false);
  const [updatingItemId, setUpdatingItemId] = React.useState<string | undefined>();

  // Build lineItem lookup map for mutations
  const lineItemMap = React.useMemo(
    () => new Map((basket?.lineItems ?? []).map((li) => [li.id, li])),
    [basket?.lineItems],
  );

  const handleQuantityChange = React.useCallback(
    (id: string, quantity: number) => {
      const lineItem = lineItemMap.get(id);
      if (!lineItem) return;
      setUpdatingItemId(id);
      updateBasket({ lineItem, quantity }).finally(() => setUpdatingItemId(undefined));
    },
    [lineItemMap, updateBasket],
  );

  const handleRemove = React.useCallback(
    (id: string) => {
      const lineItem = lineItemMap.get(id);
      if (!lineItem) return;
      setUpdatingItemId(id);
      updateBasket({ lineItem, quantity: 0 }).finally(() => setUpdatingItemId(undefined));
    },
    [lineItemMap, updateBasket],
  );

  const navigateToCheckout = async (skipFetchCheck = false) => {
    pushCheckoutLoader();

    if (skipFetchCheck) {
      try {
        window.location.href = `/${locale}/checkout`;
        return;
      } catch {
        closeCheckoutLoader();
        setCheckoutError(true);
        return;
      }
    }

    try {
      const result = await fetch(`/${locale}/checkout`);
      if (result.redirected) {
        try {
          window.location.href = `/${locale}/checkout`;
        } catch {
          closeCheckoutLoader();
          setCheckoutError(true);
        }
        return;
      }
      push(`/${locale}/checkout`);
    } catch {
      try {
        window.location.href = `/${locale}/checkout`;
      } catch {
        closeCheckoutLoader();
        setCheckoutError(true);
      }
    }
  };

  const handleCheckout = React.useCallback(async () => {
    try {
      const checkAuth = !user;
      const result = await goToCheckout({ skipSession: checkAuth });
      if (result === true) return;
      setIsAuthStep(true);
    } catch {
      /* empty */
    }
  }, [goToCheckout, user]);

  const outOfStockSkus = basket?.outOfStockItems?.map((item) => item.sku);
  const outOfStockIds = getOutOfStockIds(basket, outOfStockSkus);
  const disabled = outOfStockIds != null && outOfStockIds.length > 0;

  const { lineItems = [] } = basket || {};

  React.useEffect(() => {
    if (!inDrawer) {
      closeCheckoutLoader();
    }
  }, []);

  const cb = useDelayedCB(
    (isOpen: boolean) => {
      if (basket) {
        logCartViewed(
          `${isOpen ? 'open' : 'close'} cart`,
          basket,
          lineItems,
          basket.recommendations,
        );
      }
      if (!isOpen) {
        setTimeout(() => setIsAuthStep(false), 500);
      }
    },
    React.useCallback(() => !isLoading, [isLoading]),
    { maxTries: 30 },
  );

  useUIListener(ModalID.cart, cb);

  if (!inDrawer) {
    return <CartPageView outOfStockItems={outOfStockSkus} />;
  }

  const mappedLineItems = basket ? basketToCartLineItems(basket) : [];
  const mappedTotals = basket
    ? basketToCartTotals(basket)
    : {
        subtotal: 0,
        shippingTotal: 0,
        tax: 0,
        total: 0,
        totalItems: 0,
        currency: currency ?? 'USD',
      };

  const loggedOutTitle = t('cart.loggedOutTitle', undefined, { default: 'none' });
  const loggedOutSentence = t('cart.loggedOutSentence', undefined, { default: 'none' });

  const bodyPrefixSlot =
    checkoutError || (loggedOutTitle !== 'none' && loggedOutSentence !== 'none') ? (
      <>
        {checkoutError && (
          <ErrorState
            text={t('cart.errorStateText', undefined, {
              default: 'Something went wrong. Please try again.',
            })}
          />
        )}
        {loggedOutTitle !== 'none' && loggedOutSentence !== 'none' && (
          <>
            <h6 className="tra-text-web-heading-6 font-normal text-left pb-6">{loggedOutTitle}</h6>
            <p>{loggedOutSentence}</p>
          </>
        )}
      </>
    ) : undefined;

  const cartPromotionalMarkdown = t('cart.cartPromotionalMarkdown', undefined, { default: 'none' });
  const cartPromotionalIcon = t<ImageProps>('cart.cartPromotionalIcon', undefined, {
    returnObjects: true,
    default: undefined,
  });
  const promotionalImageUrl = buildImageSrc(cartPromotionalIcon?.image, 200, 0, 0);
  const promotionalNode =
    cartPromotionalMarkdown !== 'none' ? (
      <div className="bg-warm-200 p-6 mt-3 flex gap-4">
        <div className="w-20">
          <Image
            src={promotionalImageUrl}
            height={30}
            width={30}
            alt={cartPromotionalIcon?.altTag}
          />
        </div>
        <Markdown>{cartPromotionalMarkdown}</Markdown>
      </div>
    ) : undefined;

  const cartEmptyCTA = t<LinkProps>('cart.cartEmptyCTA.link', undefined, {
    returnObjects: true,
    default: undefined,
  });
  const emptyCtaNode = cartEmptyCTA ? (
    <Button
      variant={ButtonVariant.secondary}
      className="w-full rounded text-center"
      onClick={() => close?.()}
      as={Anchor}
      dataTestId="cart-footer-continue-shopping"
      {...cartEmptyCTA}
    >
      {cartEmptyCTA.displayName}
    </Button>
  ) : undefined;

  return (
    <SharedCart
      isLoading={isLoading}
      lineItems={mappedLineItems}
      totals={mappedTotals}
      outOfStockIds={outOfStockIds}
      updatingItemId={updatingItemId}
      disabled={disabled}
      isCheckingOut={isMutating}
      onQuantityChange={handleQuantityChange}
      onRemove={handleRemove}
      onCheckout={handleCheckout}
      fullBodyOverride={
        isAuthStep ? (
          <Auth
            onSuccess={async () => {
              try {
                await mutateCustomer();
                const goToCheckoutResult = await goToCheckout();

                // Shopify handled navigation (or showed error modal) — don't navigate further
                if (goToCheckoutResult === true) return;

                // If goToCheckout returned null (session bridge failed), use direct navigation
                const useDirectNavigation = goToCheckoutResult === null;
                navigateToCheckout(useDirectNavigation);
              } catch (e) {
                console.error(e);
                pushErrorModal();
              }
            }}
            isCheckout
          />
        ) : undefined
      }
      bodyPrefixSlot={bodyPrefixSlot}
      additionalItemsSlot={undefined}
      promotionalNode={promotionalNode}
      title={t('cart.title', undefined, { default: 'Your Cart' })}
      emptyMessage={t('cart.cartEmptyMessage', undefined, {
        default: 'Your cart is currently empty.',
      })}
      estimatedTotalLabel={t('cart.estimatedTotal', undefined, { default: 'Estimated Total' })}
      checkoutLabel={t('cart.continueToCheckoutCTA', undefined, {
        default: 'Continue to Checkout',
      })}
      loadingNode={<LoadingLogo small />}
      emptyCtaNode={emptyCtaNode}
      locale={locale}
      outOfStockMessage={t('cart.outOfStockText', undefined, {
        default: 'This item is out of stock. Remove from your cart to complete your purchase.',
      })}
      removeLabel={t('cart.cartRemoveText', undefined, { default: 'Remove' })}
      outOfStockLabel={t('productDetailPageContent.outOfStockText', undefined, {
        default: 'Out of Stock',
      })}
      subtotalLabel={t(
        'orderSummary.subtotalItems',
        { totalItems: basket?.totalItems ?? 0 },
        { default: `Subtotal ({{totalItems}} items)` },
      )}
      shippingLabel={t('cart.shipping', undefined, { default: 'Shipping' })}
      shippingFreeLabel={t('addToCartWindowContent.shippingFreeLabel', undefined, {
        default: 'Free',
      })}
      taxLabel={t('cart.estimatedTax', undefined, { default: 'Estimated Tax' })}
      taxFreeLabel={t('addToCartWindowContent.taxFreeLabel', undefined, { default: '-' })}
    />
  );
};

export default Cart;
